export const GROUPEMENT_TITLE_MIN_LETTERS = 5;
export const GROUPEMENT_TITLE_MAX_LENGTH = 40;

const GROUPEMENT_TITLE_CHAR_RE =
  /^[A-Za-zÀÂÄÆÇÉÈÊËÏÎÔŒÙÛÜŸàâäæçéèêëïîôœùûüÿ ,()]+$/;
const GROUPEMENT_LETTER_RE =
  /[A-Za-zÀÂÄÆÇÉÈÊËÏÎÔŒÙÛÜŸàâäæçéèêëïîôœùûüÿ]/g;

export function countGroupementLetters(value) {
  const matches = String(value || "").match(GROUPEMENT_LETTER_RE);
  return matches ? matches.length : 0;
}

export function sanitizeGroupementTitleInput(value) {
  return String(value || "")
    .replace(/[^A-Za-zÀÂÄÆÇÉÈÊËÏÎÔŒÙÛÜŸàâäæçéèêëïîôœùûüÿ ,()]/g, "")
    .slice(0, GROUPEMENT_TITLE_MAX_LENGTH);
}

export function capitalizeGroupementTitleFirstLetter(value) {
  const raw = String(value || "");
  const match = raw.match(
    /[A-Za-zÀÂÄÆÇÉÈÊËÏÎÔŒÙÛÜŸàâäæçéèêëïîôœùûüÿ]/
  );
  if (!match) return raw;
  const letter = match[0];
  const upper = letter.toLocaleUpperCase("fr");
  if (letter === upper) return raw;
  return raw.slice(0, match.index) + upper + raw.slice(match.index + letter.length);
}

export function normalizeGroupementTitle(value) {
  return String(value || "").replace(/\s+/g, " ").trim();
}

export function validateGroupementTitle(value) {
  const raw = String(value || "");
  if (raw.length > GROUPEMENT_TITLE_MAX_LENGTH) {
    return `Le nom ne peut pas dépasser ${GROUPEMENT_TITLE_MAX_LENGTH} caractères.`;
  }

  const normalized = normalizeGroupementTitle(raw);
  if (!normalized) {
    return "Le nom ne peut pas être vide ni composé uniquement d'espaces.";
  }
  if (!GROUPEMENT_TITLE_CHAR_RE.test(normalized)) {
    return "Seules les lettres (accents compris), les espaces, la virgule et les parenthèses sont autorisés.";
  }
  if (countGroupementLetters(normalized) < GROUPEMENT_TITLE_MIN_LETTERS) {
    return `Le nom doit contenir au moins ${GROUPEMENT_TITLE_MIN_LETTERS} lettres.`;
  }
  return null;
}

export function hexToRgb(hex) {
  const match = /^#?([0-9a-fA-F]{6})$/.exec(String(hex || "").trim());
  if (!match) return null;
  const value = Number.parseInt(match[1], 16);
  return {
    r: (value >> 16) & 255,
    g: (value >> 8) & 255,
    b: value & 255,
  };
}

export function formatRgbLabel(hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) return "rgb(—, —, —)";
  return `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`;
}

export function normalizeGroupementColor(value) {
  const raw = String(value || "").trim();
  const hex = raw.match(/^#?([0-9a-fA-F]{6})$/);
  if (hex) return `#${hex[1].toLowerCase()}`;
  return null;
}

export function pickUnusedGroupementColor(usedColors = []) {
  const used = new Set(
    usedColors.map((color) => normalizeGroupementColor(color)).filter(Boolean)
  );
  const palette = [
    "#6b4226",
    "#2f4f38",
    "#4a3454",
    "#6b3f38",
    "#2e3f52",
    "#5c4528",
    "#3a4a2a",
    "#5a2e3a",
    "#2a4a4a",
    "#4a3a28",
  ];
  const available = palette.find((color) => !used.has(color));
  if (available) return available;

  for (let hue = 12; hue < 360; hue += 17) {
    const candidate = hslToHex(hue, 38, 32);
    if (!used.has(candidate)) return candidate;
  }
  return "#39210d";
}

function hslToHex(h, s, l) {
  const sat = s / 100;
  const light = l / 100;
  const a = sat * Math.min(light, 1 - light);
  const f = (n) => {
    const k = (n + h / 30) % 12;
    const color = light - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color)
      .toString(16)
      .padStart(2, "0");
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}
